import axios from 'axios';

// 开发环境使用完整URL，生产环境使用相对路径
// 如果设置了REACT_APP_API_URL环境变量，使用该地址
// 否则根据当前访问地址自动判断
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return `${process.env.REACT_APP_API_URL}/api`;
  }
  if (process.env.NODE_ENV === 'development') {
    // 开发环境：如果通过IP访问，使用IP地址；否则使用localhost
    const hostname = window.location.hostname;
    if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
      return `http://${hostname}:5000/api`;
    }
    return 'http://localhost:5000/api';
  }
  return '/api';
};

const API_BASE_URL = getApiBaseUrl();

const AUTH_TOKEN_KEY = 'fund_nav_token';

function readStoredToken() {
  try {
    return sessionStorage.getItem(AUTH_TOKEN_KEY);
  } catch {
    return null;
  }
}
function writeStoredToken(token) {
  try {
    if (token) sessionStorage.setItem(AUTH_TOKEN_KEY, token);
    else sessionStorage.removeItem(AUTH_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

// 内存中保存当前 token，登录后立即可用
let currentToken = null;
export function setAuthToken(token) {
  currentToken = token || null;
  writeStoredToken(token);
}
export function clearAuthToken() {
  currentToken = null;
  writeStoredToken(null);
}

// 401 时静默 refresh（单飞）
let refreshPromise = null;
async function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;
  refreshPromise = axios
    .post(`${API_BASE_URL}/auth/refresh`, {}, { withCredentials: true, timeout: 20000 })
    .then((res) => {
      const body = res.data?.data || res.data;
      if (body?.success === false) throw new Error(body.error || 'refresh failed');
      const payload = body?.data || body;
      const token = payload?.token;
      if (!token) throw new Error('refresh failed');
      setAuthToken(token);
      return { token, user: payload.user };
    })
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
}

// 创建axios实例（本地冷启动/大库首次查询可能较慢，默认 90s）
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 90000,
  withCredentials: true,
});
// 命名导出 axios 实例，供新代码直接用 api.get/api.post
export { api };

// 请求拦截器：优先用内存 token，否则读 sessionStorage
api.interceptors.request.use((config) => {
  const token = currentToken || readStoredToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// 响应拦截器：401 时尝试 refresh 一次，失败再跳登录
api.interceptors.response.use(
  response => response.data,
  async (error) => {
    const config = error.config || {};
    const status = error.response?.status;
    const isAuthPath = (config.url || '').includes('/auth/login')
      || (config.url || '').includes('/auth/refresh')
      || (config.url || '').includes('/auth/logout')
      || (config.url || '').includes('/auth/change-password');

    if (status === 401 && !config._authRetry && !isAuthPath) {
      config._authRetry = true;
      try {
        await refreshAccessToken();
        const token = currentToken || readStoredToken();
        if (token) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
          return api.request(config);
        }
      } catch {
        /* fall through */
      }
    }

    // 登录页 / 改密页期间不要触发跳转，避免用户被踢出当前流程
    const path = window.location.pathname;
    const onAuthPage = path.startsWith('/admin/login') || path.startsWith('/change-password');
    if (status === 401 && !onAuthPage) {
      clearAuthToken();
      window.location.href = '/admin/login';
    }
    console.error('API Error:', error);
    if (error.code === 'ECONNABORTED') {
      return Promise.reject({
        success: false,
        error: '请求超时，后端仍在处理中，请稍等片刻后刷新页面（首次加载可能需 1～3 分钟）',
        timeoutError: true,
        networkError: true,
      });
    }
    if (error.message === 'Network Error' || !error.response) {
      return Promise.reject({
        success: false,
        error: '无法连接到后端API服务，请确保后端已启动',
        networkError: true,
      });
    }
    if (error.response) {
      return Promise.reject(error.response.data || {
        success: false,
        error: `服务器错误: ${error.response.status} ${error.response.statusText}`
      });
    }
    return Promise.reject({
      success: false,
      error: error.message || '未知错误'
    });
  }
);

export { AUTH_TOKEN_KEY, readStoredToken };

// API方法
export const fundApi = {
  // 获取基金列表
  // strategyLevel1 / strategyLevel2 支持字符串或字符串数组（多选）
  getFunds: (search = '', strategyLevel1 = [], strategyLevel2 = []) => {
    const normalizeParam = (val) => {
      if (Array.isArray(val)) {
        return val.join(',');
      }
      return val || '';
    };

    return api.get('/funds', { 
      params: { 
        search,
        strategy_level1: normalizeParam(strategyLevel1),
        strategy_level2: normalizeParam(strategyLevel2)
      },
      timeout: 180000 // 基金列表首次查询可能超过 60s（本地大库冷启动）
    });
  },

  // 轻量级基金选项（仅 code、fund_name），供对比页/组合页下拉用，加载快
  getFundOptions: (search = '') =>
    api.get('/funds/options', { params: { search }, timeout: 15000 }),

  // 按策略导出基金列表（与列表页同结构，用于导出 CSV）
  getFundsExport: (strategyLevel1 = [], strategyLevel2 = []) => {
    const normalizeParam = (val) => {
      if (Array.isArray(val)) return val.join(',');
      return val || '';
    };
    return api.get('/funds/export', {
      params: {
        strategy_level1: normalizeParam(strategyLevel1),
        strategy_level2: normalizeParam(strategyLevel2),
      },
      timeout: 120000,
    });
  },

  // 带分页的基金列表（基金列表页使用）
  getFundsPaged: (
    search = '',
    strategyLevel1 = [],
    strategyLevel2 = [],
    page = 1,
    pageSize = 20,
    sortBy = 'code',
    sortOrder = 'asc',
    managerId = '',
  ) => {
    const allowedSortBy = new Set([
      'code',
      'fund_name',
      'adjusted_nav',
      'return_1w',
      'return_ytd',
      'annualized_return_since_inception',
      'max_drawdown',
      'sharpe_ratio_since_inception',
    ]);
    const normalizedSortBy = allowedSortBy.has(sortBy) ? sortBy : 'code';
    const normalizeParam = (val) => {
      if (Array.isArray(val)) {
        return val.join(',');
      }
      return val || '';
    };
    const params = {
      search,
      strategy_level1: normalizeParam(strategyLevel1),
      strategy_level2: normalizeParam(strategyLevel2),
      page,
      page_size: pageSize,
      sort_by: normalizedSortBy,
      sort_order: sortOrder === 'desc' ? 'desc' : 'asc',
    };
    if (managerId) {
      params.manager_id = managerId;
    }
    return api.get('/funds/page', {
      params,
      timeout: 180000,
    });
  },

  getManagersPaged: (
    search = '',
    page = 1,
    pageSize = 20,
    sortBy = 'manager_name',
    sortOrder = 'asc',
    scaleRange = [],
  ) => {
    const allowedSortBy = new Set([
      'manager_name',
      'management_scale',
      'register_time',
      'main_strategy',
      'fund_count',
    ]);
    const normalizedSortBy = allowedSortBy.has(sortBy) ? sortBy : 'manager_name';
    const normalizeScaleParam = (val) => {
      if (Array.isArray(val)) {
        return val.filter(Boolean).join(',');
      }
      return val || '';
    };
    const params = {
      search,
      page,
      page_size: pageSize,
      sort_by: normalizedSortBy,
      sort_order: sortOrder === 'desc' ? 'desc' : 'asc',
    };
    const scaleParam = normalizeScaleParam(scaleRange);
    if (scaleParam) {
      params.scale_range = scaleParam;
    }
    return api.get('/managers/page', {
      params,
      timeout: 120000,
    });
  },

  getManagerDetail: (managerId) =>
    api.get(`/managers/${encodeURIComponent(managerId)}`),

  getManagerDueDiligenceReports: (managerId) =>
    api.get(`/managers/${encodeURIComponent(managerId)}/due-diligence`),

  createManagerDueDiligenceReport: (managerId, body) =>
    api.post(`/managers/${encodeURIComponent(managerId)}/due-diligence`, body),

  updateManagerDueDiligenceReport: (managerId, reportId, body) =>
    api.put(`/managers/${encodeURIComponent(managerId)}/due-diligence/${reportId}`, body),

  deleteManagerDueDiligenceReport: (managerId, reportId) =>
    api.delete(`/managers/${encodeURIComponent(managerId)}/due-diligence/${reportId}`),

  getInvestmentAnalysisStrategyReturns: () =>
    api.get('/investment-analysis/strategy-returns', {
      timeout: 180000,
    }),

  getInvestmentAnalysisIndexEnhancementValue: ({ includeIneligible = false, strategyLevel2 } = {}) => {
    const params = {};
    if (includeIneligible) params.include_ineligible = 1;
    if (strategyLevel2) params.strategy_level2 = strategyLevel2;
    return api.get('/investment-analysis/index-enhancement-value', {
      params,
      timeout: 300000,
    });
  },

  getAlphaQualityAnalysis: ({ strategyLevel2 } = {}) =>
    api.get('/investment-analysis/alpha-quality', {
      params: strategyLevel2 ? { strategy_level2: strategyLevel2 } : {},
      timeout: 300000,
    }),

  getIndexEnhancementUnified: ({ includeIneligible = true, strategyLevel2 } = {}) => {
    const params = {};
    if (includeIneligible) params.include_ineligible = 1;
    if (strategyLevel2) params.strategy_level2 = strategyLevel2;
    return api.get('/investment-analysis/index-enhancement-unified', {
      params,
      timeout: 300000,
    });
  },

  getQuantSelectUnified: ({ includeIneligible = true } = {}) => {
    const params = {};
    if (includeIneligible) params.include_ineligible = 1;
    return api.get('/investment-analysis/quant-select-unified', {
      params,
      timeout: 300000,
    });
  },

  getQuantCtaUnified: ({ includeIneligible = true } = {}) => {
    const params = {};
    if (includeIneligible) params.include_ineligible = 1;
    return api.get('/investment-analysis/quant-cta-unified', {
      params,
      timeout: 300000,
    });
  },

  getQuantMnUnified: ({ includeIneligible = true } = {}) => {
    const params = {};
    if (includeIneligible) params.include_ineligible = 1;
    return api.get('/investment-analysis/quant-mn-unified', {
      params,
      timeout: 300000,
    });
  },

  getQuantArbUnified: ({ includeIneligible = true } = {}) => {
    const params = {};
    if (includeIneligible) params.include_ineligible = 1;
    return api.get('/investment-analysis/quant-arb-unified', {
      params,
      timeout: 300000,
    });
  },

  // 收益分布页面统一入口：按 strategyLevel2 路由到对应 unified 接口
  // - 有评价体系（带评分）：走 5 个 unified；带阈值的功能按钮（如"超额"）会显示
  // - 无评价体系：走 return-distribution-basic；access=no_score，散点默认色
  getReturnDistribution: ({ strategyLevel2, includeIneligible = true } = {}) => {
    const opts = { includeIneligible };
    const ie = () => fundApi.getIndexEnhancementUnified({ ...opts, strategyLevel2 });
    const basic = () => fundApi.getReturnDistributionBasic({ strategyLevel2 });
    const map = {
      // IE 体系（含 5 个主指增 + 其他/红利/风格指增 走 basic 因不在 unified level2）
      '1000指增': ie, '500指增': ie, '300指增': ie, 'A500指增': ie, '2000指增': ie,
      '其他指增': basic, '红利指增': basic, '风格指增': basic,
      // 量化选股 / 转债
      '量化选股': () => fundApi.getQuantSelectUnified(opts),
      '量化转债': basic,
      // 市场中性体系（unified 只服务"市场中性"）
      '市场中性': () => fundApi.getQuantMnUnified(opts),
      '股票多空': basic, '灵活对冲': basic,
      // CTA 体系（unified 服务 量化CTA中长 + 量化CTA短；"量化CTA"按 l2 实际值由后端分发）
      '量化CTA': () => fundApi.getQuantCtaUnified(opts),
      '量化CTA短': () => fundApi.getQuantCtaUnified(opts),
      '股指CTA': basic,
      // 套利体系（unified 服务 6 种标准套利；"套利"为前端的总称呼）
      '套利': () => fundApi.getQuantArbUnified(opts),
      '期货套利': () => fundApi.getQuantArbUnified(opts),
      'ETF套利': () => fundApi.getQuantArbUnified(opts),
      '复合套利': () => fundApi.getQuantArbUnified(opts),
      '期权套利': () => fundApi.getQuantArbUnified(opts),
      '可转债套利': () => fundApi.getQuantArbUnified(opts),
      '期现套利': () => fundApi.getQuantArbUnified(opts),
      // 期权 / 主观 / 其他
      '期权策略': basic,
      '主观CTA': basic,
      '主观股票多头': basic,
      '主观多头': basic, // 兼容旧 DB 标签
      // 纯基础展示
      '复合策略': basic, '宏观策略': basic, '全天候': basic,
      '排板策略': basic, '择时选股': basic, '股票T0': basic,
      '期货高频': basic, '期权复合': basic, '未分类': basic,
    };
    const fn = map[strategyLevel2];
    if (fn) return fn();
    // 未在表内的二级策略 → 通用 basic（兜底）
    return basic();
  },

  getReturnDistributionBasic: ({ strategyLevel2, includeIneligible = true } = {}) => {
    const params = {};
    if (includeIneligible) params.include_ineligible = 1;
    if (strategyLevel2) params.strategy_level2 = strategyLevel2;
    return api.get('/investment-analysis/return-distribution-basic', {
      params,
      timeout: 300000,
    });
  },

  getQuantEquityCandidates: () =>
    api.get('/investment-analysis/quant-equity-candidates', { timeout: 300000 }),

  getAlphaQualityCompare: ({ codes, period = '1y' } = {}) =>
    api.get('/investment-analysis/alpha-quality/compare', {
      params: {
        codes: Array.isArray(codes) ? codes.join(',') : codes,
        period,
      },
      timeout: 120000,
    }),

  getFundAlphaAnalysis: ({ code } = {}) =>
    api.get('/fund/alpha-analysis', {
      params: { code },
      timeout: 120000,
    }),

  getFundAlphaNavSeries: ({ code, period = '1y' } = {}) =>
    api.get('/fund/alpha-analysis', {
      params: { code, nav_only: 1, period },
      timeout: 60000,
    }),

  getIndexEnhancementStrategies: () =>
    api.get('/investment-analysis/index-enhancement-value/strategies', {
      timeout: 60000,
    }),

  getIndexEnhancementValueConfig: () =>
    api.get('/investment-analysis/index-enhancement-value/config'),

  saveIndexEnhancementValueConfig: (config) =>
    api.put('/investment-analysis/index-enhancement-value/config', { config }),

  resetIndexEnhancementValueConfig: () =>
    api.post('/investment-analysis/index-enhancement-value/config/reset'),

  getMarketNeutralValue: ({ includeIneligible = false } = {}) =>
    api.get('/investment-analysis/market-neutral-value', {
      params: includeIneligible ? { include_ineligible: 1 } : {},
      timeout: 300000,
    }),

  getMarketNeutralValueConfig: () =>
    api.get('/investment-analysis/market-neutral-value/config'),

  saveMarketNeutralValueConfig: (config) =>
    api.put('/investment-analysis/market-neutral-value/config', { config }),

  resetMarketNeutralValueConfig: () =>
    api.post('/investment-analysis/market-neutral-value/config/reset'),

  getQuantCtaValue: ({ includeIneligible = false, strategyLevel2 } = {}) =>
    api.get('/investment-analysis/quant-cta-value', {
      params: {
        ...(includeIneligible ? { include_ineligible: 1 } : {}),
        ...(strategyLevel2 ? { strategy_level2: strategyLevel2 } : {}),
      },
      timeout: 300000,
    }),

  getQuantCtaValueConfig: () =>
    api.get('/investment-analysis/quant-cta-value/config'),

  saveQuantCtaValueConfig: (config) =>
    api.put('/investment-analysis/quant-cta-value/config', { config }),

  resetQuantCtaValueConfig: () =>
    api.post('/investment-analysis/quant-cta-value/config/reset'),

  // CTA 环境：子策略适配矩阵 + 品种强弱 + 板块热力
  getCtaEnvV3Dashboard: ({ days = 250 } = {}) =>
    api.get('/cta-env/v3/dashboard', { params: { days }, timeout: 120000 }),

  // 列表页近一年业绩缩略图（批量）
  getFundSparklines: (codes = [], points = 40) => {
    const list = Array.isArray(codes) ? codes.filter(Boolean) : [];
    if (!list.length) {
      return Promise.resolve({ success: true, data: {} });
    }
    return api.get('/funds/sparklines', {
      params: { codes: list.join(','), points },
      timeout: 60000,
    });
  },

  // 获取基金详情
  getFundDetail: (code) => 
    api.get(`/fund/${code}`),

  // 更新基金自定义标签（仅管理员/系统管理员）
  updateFundCustomTag: (code, customTag) =>
    api.put(`/fund/${code}/custom-tag`, { custom_tag: customTag || '' }),

  // 更新基金策略标签（仅管理员/系统管理员）
  updateFundStrategyTags: (code, strategyLevel1, strategyLevel2) =>
    api.put(`/fund/${code}/strategy-tags`, {
      strategy_level1: strategyLevel1 || '',
      strategy_level2: strategyLevel2 || '',
    }),

  // 批量更新基金策略标签（仅管理员/系统管理员）
  batchUpdateFundStrategyTags: (items = []) =>
    api.put('/strategies/batch', { items }),

  // 基金档案：展示名称、可投情况、费率（登录用户）
  updateFundProfile: (code, payload) =>
    api.put(`/fund/${code}/profile`, payload),

  // 追加基金备注（登录用户）
  addFundNote: (code, content) =>
    api.post(`/fund/${code}/notes`, { content }),

  // 更新管理人主营策略（登录用户）
  updateFundManagerMainStrategy: (code, mainStrategy) =>
    api.put(`/fund/${code}/manager-main-strategy`, { main_strategy: mainStrategy || null }),

  // 管理人选项（管理员）
  getManagerOptions: (q = '', limit = 50) =>
    api.get('/managers/options', { params: { q, limit } }),

  // 手动指定基金管理人（管理员）
  updateFundManager: (code, managerId) =>
    api.put(`/fund/${code}/manager`, { manager_id: managerId }),

  // 基金净值日期范围（组合页选起始日期时限定在该基金有数据的区间内）
  getFundNavRange: (code) =>
    api.get(`/fund/${code}/nav-range`),

  // 获取净值序列
  getNavSeries: (code, startDate, endDate, format = 'chart') =>
    api.get(`/fund/${code}/nav`, {
      params: { start_date: startDate, end_date: endDate, format }
    }),

  // 同类产品分析（完整同类池 + 排名分位 + 收益分布 + 散点坐标）
  // window: 统计周期（1m/3m/6m/ytd/1y/2y/3y/5y/si），固定周期为同区间对比
  getPeerAnalysis: (code, window = '1y') =>
    api.get(`/fund/${code}/peer-analysis`, { params: { window } }),

  // 获取业绩指标
  getPerformance: (code, periodType, includeIndex = false, indexCode = null, startDate = null, endDate = null) => {
    const params = {};
    if (periodType) {
      params.period_type = periodType;
    }
    if (includeIndex && indexCode) {
      params.include_index = 'true';
      params.index_code = indexCode;
    }
    if (startDate) {
      params.start_date = startDate;
    }
    if (endDate) {
      params.end_date = endDate;
    }
    return api.get(`/fund/${code}/performance`, { params });
  },

  // 获取回撤曲线
  getDrawdown: (code, startDate, endDate) => 
    api.get(`/fund/${code}/drawdown`, { 
      params: { start_date: startDate, end_date: endDate } 
    }),

  // ========== 产品详情页增强分析（自建计算） ==========
  // 一次性获取全部分析结果（回撤明细/月度热力图/年度对比/滚动指标/收益分布）
  getAnalyticsOverview: (code, params = {}) =>
    api.get(`/fund/${code}/analytics/overview`, { params }),

  // 历史回撤明细（前 N 大回撤：区间/幅度/起止日/修复天数）
  getAnalyticsDrawdownDetail: (code, params = {}) =>
    api.get(`/fund/${code}/analytics/drawdown-detail`, { params }),

  // 月度收益矩阵（年 × 月，热力图）
  getAnalyticsMonthlyReturns: (code, params = {}) =>
    api.get(`/fund/${code}/analytics/monthly-returns`, { params }),

  // 年度收益对比（产品 vs 基准 vs 同策略）
  getAnalyticsYearlyReturns: (code, params = {}) =>
    api.get(`/fund/${code}/analytics/yearly-returns`, { params }),

  // 滚动窗口指标曲线
  getAnalyticsRollingMetrics: (code, params = {}) =>
    api.get(`/fund/${code}/analytics/rolling-metrics`, { params }),

  // 收益分布特征（涨跌占比/偏度峰度/VaR/CVaR）
  getAnalyticsReturnDistribution: (code, params = {}) =>
    api.get(`/fund/${code}/analytics/return-distribution`, { params }),

  // 自定义区间业绩
  getCustomPerformance: (code, startDate, endDate) => 
    api.post(`/fund/${code}/custom`, { 
      start_date: startDate, 
      end_date: endDate 
    }),

  // 基金对比（可选 startDate/endDate，传入时返回该区间的区间收益、年化、波动率、回撤、夏普）
  compareFunds: (codes, periodType = 'all_time', periodName = 'all', startDate = null, endDate = null) => 
    api.post('/compare', { 
      codes, 
      period_type: periodType, 
      period_name: periodName,
      ...(startDate && endDate ? { start_date: startDate, end_date: endDate } : {}),
    }),

  // 业绩排名
  getRanking: (metric = 'annualized_return', periodType = 'all_time', topN = 20, groupByStrategy = true, strategyLevel2 = null) => 
    api.get('/ranking', { 
      params: { 
        metric, 
        period_type: periodType, 
        top_n: topN,
        group_by_strategy: groupByStrategy,
        ...(strategyLevel2 ? { strategy_level2: strategyLevel2 } : {}),
      } 
    }),

  // 系统统计
  getStatistics: () => 
    api.get('/statistics'),
  
  // 获取策略标签
  getStrategies: () =>
    api.get('/strategies'),
  
  // 获取策略统计
  getStrategyStats: () =>
    api.get('/strategies/stats'),
  
  // 获取指数数据
  getIndexData: (indexCode, startDate, endDate) =>
    api.get('/index', {
      params: {
        code: indexCode,
        start_date: startDate,
        end_date: endDate
      }
    }),
  
  // 获取所有指数列表
  getIndexList: () =>
    api.get('/index/list'),
  
  // 获取 CTA 商品基准（库内代码 NHCI = 南华商品指数，Tushare NHCI.NH）
  getCommodityIndex: (startDate, endDate) =>
    api.get('/index', {
      params: {
        code: 'NHCI',
        start_date: startDate,
        end_date: endDate
      }
    }),
  /** @deprecated 请用 getCommodityIndex */
  getNanhuaIndex: (startDate, endDate) =>
    api.get('/index', {
      params: {
        code: 'NHCI',
        start_date: startDate,
        end_date: endDate
      }
    }),
  
  // 获取股指期货升贴水率
  getFuturesPremium: (startDate, endDate) =>
    api.get('/index/futures-premium', {
      params: {
        start_date: startDate,
        end_date: endDate
      }
    }),
  
  // 获取股指期货升贴水明细（IC/IM/IF/IH × 当月/次月/当季/次季）
  getFuturesBasis: (futuresCode, term, startDate, endDate) =>
    api.get('/futures/basis', {
      params: {
        futures_code: futuresCode,
        term: term,
        start_date: startDate,
        end_date: endDate
      }
    }),
  
  // 获取按二级策略统计的近一周收益表现（总览页用）
  getStrategyPerformanceSummary: () =>
    api.get('/strategies/performance-summary', { timeout: 180000 }),

  // 获取按一级策略分组的业绩指标汇总（总览页新模块用，window: YTD/1Y/1M/1W）
  getStrategyLevel1Overview: ({ window = 'YTD' } = {}) =>
    api.get('/strategies/level1-overview', {
      params: { window },
      timeout: 180000,
    }),

  // 组合相关接口（按“某日投入多少”保存；每账号独立；业绩按最新净值实时计算）
  savePortfolio: (name, description, entries, portfolioId = null) =>
    api.post('/portfolio/save', {
      name,
      description,
      entries,
      id: portfolioId || undefined,
    }),

  getPortfolioList: () =>
    api.get('/portfolio/list'),

  getPortfolioDetail: (id) =>
    api.get(`/portfolio/${id}`),

  deletePortfolio: (id) =>
    api.delete(`/portfolio/${id}`),

  // ===== FOF 投后管理（基于 fof_book / fof_holding / fof_live_nav） =====
  getPostInvestmentBooks: () =>
    api.get('/fof-post-investment/books'),

  getPostInvestmentHoldings: (bookId) =>
    api.get(`/fof-post-investment/book/${bookId}/holdings`),

  getPostInvestmentAttribution: (bookId, window = 'ytd') =>
    api.get(`/fof-post-investment/book/${bookId}/attribution`, { params: { window } }),

  getPostInvestmentRisk: (bookId, targetVol = 0.08) =>
    api.get(`/fof-post-investment/book/${bookId}/risk`, { params: { target_vol: targetVol } }),

  getPostInvestmentDrift: (bookId) =>
    api.get(`/fof-post-investment/book/${bookId}/drift`),

  getPostInvestmentMacroTrace: (bookId) =>
    api.get(`/fof-post-investment/book/${bookId}/macro-trace`),

  // 策略回顾 / 策略报告指标快照
  getStrategyReportSnapshots: ({ strategyLevel1 = 'CTA', asOf, reportType = 'weekly', activeOnly = false } = {}) =>
    api.get('/strategy-report/snapshots', {
      params: {
        strategy_level1: strategyLevel1,
        ...(asOf ? { as_of: asOf } : {}),
        report_type: reportType,
        ...(activeOnly ? { active_only: '1' } : {}),
      },
      timeout: 120000,
    }),

  getStrategyReportLevel1Keys: () =>
    api.get('/strategy-report/level1-keys', { timeout: 60000 }),

  refreshStrategyReportSnapshots: (strategyLevel1 = 'CTA', asOf, reportType = 'weekly', { refreshAll = false } = {}) =>
    api.post('/strategy-report/refresh', {
      ...(refreshAll ? { all: true } : { strategy_level1: strategyLevel1 }),
      ...(asOf ? { as_of: asOf } : {}),
      report_type: reportType,
    }, { timeout: refreshAll ? 900000 : 300000 }),

  getWeeklyReview: ({ strategyLevel1 = 'CTA', asOf, activeOnly = false, reportType = 'weekly' } = {}) =>
    api.get('/weekly-review', {
      params: {
        strategy_level1: strategyLevel1,
        report_type: reportType,
        ...(asOf ? { as_of: asOf } : {}),
        ...(activeOnly ? { active_only: '1' } : {}),
      },
      timeout: 120000,
    }),

  getWeeklyReviewPdfTemplates: () => api.get('/weekly-review/export/templates'),

  /** 策略回顾 PDF（返回 Blob） */
  downloadWeeklyReviewPdf: async ({
    strategyLevel1 = 'CTA',
    asOf,
    activeOnly = false,
    reportType = 'weekly',
    scope = 'all',
    strategyLevel2,
    template,
    managers,
    managerNameMode = 'short',
  } = {}) => {
    const token = currentToken || localStorage.getItem(AUTH_TOKEN_KEY);
    const res = await axios.get(`${API_BASE_URL}/weekly-review/export/pdf`, {
      params: {
        strategy_level1: strategyLevel1,
        report_type: reportType,
        scope,
        ...(asOf ? { as_of: asOf } : {}),
        ...(activeOnly ? { active_only: '1' } : {}),
        ...(strategyLevel2 ? { strategy_level2: strategyLevel2 } : {}),
        ...(template ? { template } : {}),
        ...(managers?.length ? { managers: managers.join(',') } : {}),
        ...(managerNameMode ? { manager_name_mode: managerNameMode } : {}),
      },
      responseType: 'blob',
      timeout: 180000,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return res.data;
  },

  setWeeklyReviewStar: (reportWednesday, code, starred) =>
    api.put('/weekly-review/star', {
      report_wednesday: reportWednesday,
      code,
      starred: !!starred,
    }),

  getWeeklyReviewStarSummary: ({ startWednesday, endWednesday } = {}) =>
    api.get('/weekly-review/star-summary', {
      params: {
        ...(startWednesday ? { start_wednesday: startWednesday } : {}),
        ...(endWednesday ? { end_wednesday: endWednesday } : {}),
      },
    }),

  /** weekReportWednesday：该列对应的报告周三（与近四周滚动锚定），不再按页面周+slot 存 */
  saveWeeklyReviewItemNote: (reportWednesday, code, weekReportWednesday, note) =>
    api.put('/weekly-review/item-note', {
      report_wednesday: reportWednesday,
      code,
      week_report_wednesday: weekReportWednesday,
      note: note ?? '',
    }),

  // ---------- 代销产品清单 & 周度业绩跟踪 ----------
  /** 代销清单列表（含最新业绩） */
  getDistributionFunds: ({ includeExpired = false } = {}) =>
    api.get('/distribution/funds', {
      params: includeExpired ? { include_expired: '1' } : {},
    }),

  /** 加入代销清单（单只或批量） */
  addDistributionFunds: ({ codes, channel, note, effectiveDate } = {}) =>
    api.post('/distribution/funds', {
      codes,
      ...(channel ? { channel } : {}),
      ...(note ? { note } : {}),
      ...(effectiveDate ? { effective_date: effectiveDate } : {}),
    }),

  /** 移出代销清单 */
  removeDistributionFund: (code) => api.delete(`/distribution/funds/${code}`),

  /** 批量增删代销标记 */
  bulkDistributionFunds: ({ codes, action, channel, note, effectiveDate } = {}) =>
    api.post('/distribution/funds/bulk', {
      codes,
      action,
      ...(channel ? { channel } : {}),
      ...(note ? { note } : {}),
      ...(effectiveDate ? { effective_date: effectiveDate } : {}),
    }),

  /** 周度业绩跟踪（实时计算） */
  // reportDate：显式指定要回顾的报告周（周五）；不传则由后端按「周二披露上周五净值」的节奏取最新一周
  getDistributionWeekly: ({ asOf, reportDate, excludeDistribution = true, strategyLevel2 } = {}) =>
    api.get('/distribution/weekly', {
      params: {
        ...(asOf ? { as_of: asOf } : {}),
        ...(reportDate ? { report_date: reportDate } : {}),
        exclude_distribution: excludeDistribution ? '1' : '0',
        ...(strategyLevel2 ? { strategy_level2: strategyLevel2 } : {}),
      },
      timeout: 120000,
    }),

  /** 重算并落库周度快照 */
  refreshDistributionWeekly: ({ asOf, reportDate, excludeDistribution = true } = {}) =>
    api.post('/distribution/weekly/refresh', {
      ...(asOf ? { as_of: asOf } : {}),
      ...(reportDate ? { report_date: reportDate } : {}),
      exclude_distribution: excludeDistribution,
    }, { timeout: 300000 }),

  /** 可用历史报告周列表 */
  getDistributionWeeklyDates: () => api.get('/distribution/weekly/dates'),

  /**
   * 代销产品「风险收益类」业绩指标（年化收益 / 波动 / 回撤 / 夏普 / 索提诺）
   * + 同策略池的分位对比。
   * window：指标窗口（3m / 6m / ytd / 1y / 2y / 3y / si）
   */
  getDistributionWeeklyMetrics: ({
    window = '1y',
    excludeDistribution = true,
    strategyLevel2,
  } = {}) =>
    api.get('/distribution/weekly/metrics', {
      params: {
        window,
        exclude_distribution: excludeDistribution ? '1' : '0',
        ...(strategyLevel2 ? { strategy_level2: strategyLevel2 } : {}),
      },
      timeout: 120000,
    }),

  /** 代销产品业绩走势（产品净值曲线 + 业绩比较基准曲线） */
  getDistributionWeeklyCurves: ({
    window = '1Y',
    reportDate,
    codes,
    strategyLevel2,
  } = {}) =>
    api.get('/distribution/weekly/curves', {
      params: {
        window,
        ...(reportDate ? { report_date: reportDate } : {}),
        ...(codes && codes.length ? { codes: codes.join(',') } : {}),
        ...(strategyLevel2 ? { strategy_level2: strategyLevel2 } : {}),
      },
      timeout: 120000,
    }),

  /** 代销周报 PDF 导出（返回 Blob） */
  downloadDistributionWeeklyPdf: async ({
    asOf,
    reportDate,
    excludeDistribution = true,
    strategyLevel2,
    metricsWindow = '1y',
  } = {}) => {
    const token = currentToken || localStorage.getItem(AUTH_TOKEN_KEY);
    const res = await axios.get(`${API_BASE_URL}/distribution/weekly/export/pdf`, {
      params: {
        ...(asOf ? { as_of: asOf } : {}),
        ...(reportDate ? { report_date: reportDate } : {}),
        exclude_distribution: excludeDistribution ? '1' : '0',
        ...(strategyLevel2 ? { strategy_level2: strategyLevel2 } : {}),
        metrics_window: metricsWindow,
      },
      responseType: 'blob',
      timeout: 180000,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return res.data;
  },

  getFofPoolCodes: () => api.get('/fof-desk/pools'),  addFofFocus: (code) => api.post('/fof-desk/pools', { code, pool: 'focus' }),
  removeFofPool: (code) => api.delete('/fof-desk/pools/' + encodeURIComponent(code)),

  // ─── FOF 最终入选池（支持人工 override）───────────────────────────
  getFofUniverseOverview: () => api.get('/fof-desk/universe/overview'),
  upsertFofUniverseOverride: (payload) => api.post('/fof-desk/universe/override', payload),
  deleteFofUniverseOverride: (code) => api.delete('/fof-desk/universe/override/' + encodeURIComponent(code)),

  // ─── FOF 优化器 ──────────────────────────────────────────────────────
  getFofOptimizerUniverse: (params = {}) => api.get('/fof-desk/optimizer/universe', { params }),
  prepareFofOptimizerRisk: (payload) => api.post('/fof-desk/optimizer/prepare', payload),
  solveFofOptimizer: (payload) => api.post('/fof-desk/optimizer/solve', payload),
  getFofOptimizerConfig: () => api.get('/fof-desk/optimizer/config'),

  // ─── 宏观信号体系 ─────────────────────────────────────────────────
  getMacroState: (refresh = false) =>
    api.get('/macro/state', { params: refresh ? { refresh: 1 } : {} }),
  previewMacroBounds: (payload) => api.post('/macro/preview-bounds', payload),
  macroBacktest: (payload) => api.post('/macro/backtest', payload, { timeout: 600000 }),
  macroBacktestMulti: (payload) => api.post('/macro/backtest/multi', payload, { timeout: 600000 }),
  macroHistoricalBias: (asOf) => api.post('/macro/historical-bias', { as_of: asOf }, { timeout: 60000 }),

  // ─── FOF 账本（写入研究账本）───────────────────────────────────────
  getFofBooks: () => api.get('/fof-desk/books'),
  searchFofFunds: (q = '', limit = 20) => api.get('/fof-desk/books/funds/search', { params: { q, limit } }),
  addFofBookHoldings: (bookId, payload) => api.post(`/fof-desk/books/${bookId}/holdings`, payload),
  allocateFofBookHoldings: (bookId, payload) => api.post(`/fof-desk/books/${bookId}/holdings/allocate`, payload),

  // 火富牛私募周报（管理员/系统管理员）
  uploadHuofuniuWeeklyExcel: (file, reportDate = '') => {
    const form = new FormData();
    form.append('file', file);
    if (reportDate) form.append('report_date', reportDate);
    return api.post('/huofuniu/weekly/upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },

  getHuofuniuWeeklyLatest: () =>
    api.get('/huofuniu/weekly/latest'),

  getHuofuniuWeeklyData: ({ reportDate, strategyLevel1, strategyLevel2, q } = {}) =>
    api.get('/huofuniu/weekly/data', {
      params: {
        ...(reportDate ? { report_date: reportDate } : {}),
        ...(strategyLevel1 ? { strategy_level1: strategyLevel1 } : {}),
        ...(strategyLevel2 ? { strategy_level2: strategyLevel2 } : {}),
        ...(q ? { q } : {}),
      },
    }),

  // 登录与当前用户
  login: (username, password) =>
    api.post('/auth/login', { username, password }),

  changePassword: (changeToken, newPassword) =>
    api.post('/auth/change-password', { change_token: changeToken, new_password: newPassword }),

  logout: () =>
    api.post('/auth/logout'),

  getMe: () =>
    api.get('/auth/me'),

  // 首页策略观察偏好（按账号存储）
  getDashboardPreferences: () =>
    api.get('/user/dashboard-preferences'),

  saveDashboardPreferences: (strategyKeys) =>
    api.put('/user/dashboard-preferences', { strategy_keys: strategyKeys }),

  // 用户管理（仅 system_admin）
  getUsers: () =>
    api.get('/users'),

  // 每日运维看板（仅 system_admin）
  getDailyOps: (day, limit = 500) =>
    api.get('/admin/daily-ops', {
      params: { day, limit },
      timeout: 60000,
    }),

  // 未匹配管理人（仅 system_admin）
  getUnmatchedManagers: ({ page = 1, page_size = 50, q = '' } = {}) =>
    api.get('/admin/unmatched-managers', {
      params: { page, page_size, q: q || undefined },
      timeout: 60000,
    }),

  rebuildMainStrategyDefaults: (dryRun = false) =>
    api.post('/admin/main-strategy/rebuild-defaults', { dry_run: dryRun }, { timeout: 120000 }),

  getPermissionsCatalog: (targetRole = 'user') =>
    api.get('/permissions/catalog', { params: { target_role: targetRole } }),

  createUser: (payload) =>
    api.post('/users', payload),

  updateUser: (id, data) =>
    api.patch(`/users/${id}`, data),

  deleteUser: (id) =>
    api.delete(`/users/${id}`),

  getUserHistory: (id, limit = 100) =>
    api.get(`/users/${id}/auth-events`, { params: { limit } }),
};

export default fundApi;

